import { toast } from "react-toastify";
import { FileTypeContentEnum } from "./enum/FileTypeContentEnum";
import { getTranslateByKey } from "./utils";
import { QuoteSaveIntegrationRequest } from "../models/request";

export function base64ToBlobPDF(base64: any) {
	const bytes = atob(base64);
	let { length } = bytes;
	const out = new Uint8Array(length);

	while (length--) {
		out[length] = bytes.charCodeAt(length);
	}

	return new Blob([out], { type: "application/pdf" });
}

interface IConvertImageToBase64 {
	type: string;
	base64Image: string;
	name?: string;
}
export function convertImageToBase64(image: File): Promise<IConvertImageToBase64> {
	return new Promise((resolve) => {
		if (image.type && image.type.startsWith("image")) {
			const reader = new FileReader();
			reader.onload = () => {
				if (reader.result && typeof reader.result === "string") {
					const base64Image = reader.result.split(",")[1];
					const result: IConvertImageToBase64 = {
						base64Image,
						type: image.type,
						name: image.name.replace(/[^A-Za-z0-9.]/g, ""),
					};
					resolve(result);
				}
			};
			reader.readAsDataURL(image);
		} else {
			resolve(image as any);
		}
	});
}

export function getBase64(file: File) {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.readAsDataURL(file);
		reader.onload = () => resolve(reader.result);
		reader.onerror = (error) => reject(error);
	});
}

export function base64ToArrayBuffer(
	base64: any,
	type: any = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
) {
	const binaryString = window.atob(base64[0]);
	const binaryLen = binaryString.length;
	const bytes = new Uint8Array(binaryLen);
	for (let i = 0; i < binaryLen; i++) {
		const ascii = binaryString.charCodeAt(i);
		bytes[i] = ascii;
	}

	return saveByteArray(base64[1], bytes, type);
}

export function saveByteArray(
	reportName: any,
	byte: any,
	type: any = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
) {
	const blob = new Blob([byte], {
		type: type,
	});
	const link = document.createElement("a");
	link.href = window.URL.createObjectURL(blob);
	link.download = reportName;
	link.click();
}

export function downloadFile(
	fileByArray: string,
	fileName: string,
	type: any = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
) {
	const file = [fileByArray, fileName];
	base64ToArrayBuffer(file, type);
}

export function downloadFileExcel(data: any, fileName: string) {
	if (data !== null) {
		const blob = new Blob([data], { type: "application/xlsx" });
		const downloadLink = document.createElement("a");
		downloadLink.download = fileName;
		if ((window as any).webkitURL != null) {
			downloadLink.href = (window as any).webkitURL.createObjectURL(blob);
		} else {
			downloadLink.href = window.URL.createObjectURL(blob);
			downloadLink.style.display = "none";
			document.body.appendChild(downloadLink);
		}
		downloadLink.click();
	}
}

export function viewPdfNewTab(base64: string) {
	const byteCharacters = atob(base64);
	const byteNumbers = new Array(byteCharacters.length);
	for (let i = 0; i < byteCharacters.length; i++) {
		byteNumbers[i] = byteCharacters.charCodeAt(i);
	}
	const byteArray = new Uint8Array(byteNumbers);
	const file = new Blob([byteArray], { type: "application/pdf;base64" });
	const fileURL = URL.createObjectURL(file);
	window.open(fileURL);
}

export function generatePDFlink(base64: string) {
	if (!base64 || typeof base64 !== "string") {
		toast.error(getTranslateByKey("invalidBase64PdfData", true));
		return;
	}
	const binaryPdf = atob(base64);
	const pdfBlob = new Blob([new Uint8Array([...binaryPdf].map((char) => char.charCodeAt(0)))], {
		type: FileTypeContentEnum.PDF,
	});
	const pdfUrl = URL.createObjectURL(pdfBlob);
	return pdfUrl;
}

export function downloadPdfFile(codeString: string, nameFile: string) {
	const linkSource = `data:application/pdf;base64,${codeString}`;
	const downloadLink = document.createElement("a");
	const fileName = nameFile;
	downloadLink.href = linkSource;
	downloadLink.download = fileName;
	downloadLink.click();
}

export function downloadAnyFile(codeString: string, nameFile: string, type: string) {
	const blob = new Blob([codeString], { type: type });
	const anchor = document.createElement("a");
	anchor.href = URL.createObjectURL(blob);
	anchor.download = nameFile;
	anchor.click();
	URL.revokeObjectURL(anchor.href);
}

export function toBase64(file: File) {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.readAsDataURL(file);
		reader.onload = () => resolve(reader.result);
		reader.onerror = reject;
	});
}

export function downloadXML(response: string, fileName: string) {
	const decodedXml = atob(response);
	const byteArray = new Uint8Array(decodedXml.length);
	for (let i = 0; i < decodedXml.length; i++) {
		byteArray[i] = decodedXml.charCodeAt(i);
	}
	const blob = new Blob([byteArray], { type: "application/xml;charset=utf-8" });
	const url = URL.createObjectURL(blob);
	const link = document.createElement("a");

	link.href = url;
	link.download = fileName;
	link.click();

	URL.revokeObjectURL(url);
}

export async function readFileAsText(file: File): Promise<string> {
	return new Promise<string>((resolve, reject) => {
		const fileReader = new FileReader();

		fileReader.onload = function (event) {
			if (event.target?.result) {
				resolve(event.target.result as string);
			} else {
				reject(new Error(getTranslateByKey("information.invalidFile", true)));
			}
		};
		fileReader.onerror = function (_error) {
			reject(new Error(getTranslateByKey("information.invalidFile", true)));
		};
		fileReader.readAsText(file, "UTF-8");
	});
}

export async function convertFileToJSON(file: File): Promise<any> {
	const fileContents = await readFileAsText(file);
	const fileJson = JSON.parse(fileContents) as QuoteSaveIntegrationRequest;
	fileJson.FileName = file.name;
	const wanFileJson = fileJson.Summary?.AssessmentSummary?.WAN;
	const isEmptyWan = wanFileJson === null || wanFileJson === undefined || wanFileJson == "";

	if (isEmptyWan) {
		const messageError = `${getTranslateByKey("information.errorimportquote", true).replace(
			"{0}",
			file.name,
		)}: ${getTranslateByKey("information.requiredWan", true)}`;
		toast.error(messageError);
	}

	return fileJson;
}
/**
 * Function to download a file in memory (not in disk)
 * @description This function is used to download a file in memory, this is used to download a file in memory and then upload it to the server
 * @param file - File to download in memory
 * @returns
 */
export async function downloadFileMemory(file: File): Promise<File> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();

		reader.onload = (event) => {
			const result = event.target?.result as ArrayBuffer;
			const arrayBufferToFile = new File([result], file.name, {
				type: file.type,
			});
			resolve(arrayBufferToFile);
		};

		reader.onerror = (error) => {
			reject(error);
		};

		reader.readAsArrayBuffer(file);
	});
}
